import React, { useMemo, useState } from "react";
import MaterialIcon from "./MaterialIcon";
import ProjectPicker from "./ProjectPicker";
import ProjectConfirmModal from "./ProjectConfirmModal";
import NewRoomModal from "./NewRoomModal";
import EmailClassifyBusyOverlay from "./EmailClassifyBusyOverlay";
import { findExactProject, findCloseProject } from "../utils/projectMatch";
import { classifyProgressLabel } from "../utils/emailClassifyWithProgress";
import { classifyMessageId } from "../helper/emailBodyFormat";
import "./EmailAssignModal.css";

/** Resolve a stable message id from any of the id fields the API may use. */
function resolveEmailId(email) {
  return classifyMessageId(email);
}

/**
 * EmailAssignModal
 *
 * Popup opened from the "Associate to project" button on an inbox row (both the
 * dedicated e-mail section and the combined messages+e-mail view). Lets the user
 * file the email into a project (an existing project via the type-ahead picker,
 * or a brand-new name typed in the field) or mark it as SPAM. When a typed name
 * is very close to an existing project the user is asked to re-confirm.
 * A brand-new name opens the new-room step (External vs Internal, then Kjuup+
 * company if needed).
 * Reuses the shared .email-classify-* styles.
 */
function EmailAssignModal({ email, proposedName, buildings, onClassify, onClose, strings = {} }) {
  const [projectName, setProjectName] = useState(proposedName || "");
  const [selectedBuildingId, setSelectedBuildingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busyDecision, setBusyDecision] = useState(null);
  const [progress, setProgress] = useState(null);
  const [confirm, setConfirm] = useState(null); // { suggestion }
  const [newRoom, setNewRoom] = useState(false); // brand-new room + company step
  const emailId = resolveEmailId(email);
  const from = email?.fromAddress || {};
  const senderName = from.name || from.email || from.address || email?.fromName || "";
  const senderEmail = from.email || from.address || "";

  const runClassify = async (decision, buildingId, companyId, nameOverride, isInternal) => {
    if (busy) return;
    if (!emailId) return;
    setBusy(true);
    setBusyDecision(decision);
    setProgress({ completed: 0, total: 0, phase: decision === "spam" ? "spam" : "saving" });
    await new Promise((resolve) => setTimeout(resolve, 0));
    try {
      const name = (nameOverride != null ? nameOverride : projectName).trim();
      await onClassify(emailId, decision, name, buildingId, companyId, isInternal, setProgress);
      onClose();
    } catch (e) {
      setBusy(false);
      setBusyDecision(null);
      setProgress(null);
    }
  };

  // Decide how to handle the current text: existing (id) -> close match (ask)
  // -> create new. A brand-new name never gets created silently: we open the
  // "new room" step so the user can (optionally) pick a company connection.
  const submitProject = () => {
    if (busy) return;
    const name = projectName.trim();
    if (!name) return;
    if (selectedBuildingId) {
      runClassify("project", selectedBuildingId);
      return;
    }
    const exact = findExactProject(name, buildings);
    if (exact) {
      runClassify("project", exact.id);
      return;
    }
    const close = findCloseProject(name, buildings);
    if (close) {
      setConfirm({ suggestion: close });
      return;
    }
    setNewRoom(true); // brand-new project -> ask for company connection
  };

  const willCreateNew = useMemo(() => {
    const name = projectName.trim();
    if (!name || selectedBuildingId) return false;
    return !findExactProject(name, buildings);
  }, [projectName, selectedBuildingId, buildings]);

  return (
    <div className="email-classify-overlay" role="dialog" aria-modal="true" onClick={busy ? undefined : onClose}>
      <div className={`email-classify-modal${busy ? " email-classify-modal--busy" : ""}`} onClick={(e) => e.stopPropagation()}>
        <div className="email-classify-header">
          <div className="email-classify-title">
            <MaterialIcon icon="rule" size={20} color="#e066ed" />
            <span>{strings.email_classify_title || "Classify new sender"}</span>
          </div>
          <button type="button" className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral kj-btn--email" onClick={onClose} disabled={busy} title={strings.cancel || "Close"}>
            <MaterialIcon icon="close" size={18} />
          </button>
        </div>

        <div className="email-classify-email">
          <div className="email-classify-sender">
            <span className="email-classify-sender-name">{senderName}</span>
            {senderEmail && senderName !== senderEmail && (
              <span className="email-classify-sender-email">{senderEmail}</span>
            )}
          </div>
          <div className="email-classify-subject">{email?.subject || (strings.email_no_subject || "(No subject)")}</div>
        </div>

        <div className="email-classify-section">
          <div className="email-classify-question">
            {strings.email_classify_question_project || "Is this email related to a project?"}
          </div>
          <ProjectPicker
            value={projectName}
            buildings={buildings}
            disabled={busy}
            autoFocus
            onChange={(v) => {
              setProjectName(v);
              setSelectedBuildingId(null);
            }}
            onSelectExisting={(b) => {
              setProjectName(b.name);
              setSelectedBuildingId(b.id);
            }}
            onEnter={submitProject}
            strings={strings}
          />
          <button
            className="kj-btn kj-btn--primary kj-btn--email kj-btn--block email-classify-btn"
            onClick={submitProject}
            disabled={busy || !projectName.trim()}
          >
            {busy && busyDecision === "project" ? (
              <span className="email-classify-btn-spinner" aria-hidden="true" />
            ) : (
              <MaterialIcon icon={willCreateNew ? "add" : "folder"} size={16} />
            )}
            <span>
              {busy && busyDecision === "project"
                ? (strings.email_associating_to_project || "Saving email to project...")
                : willCreateNew
                  ? (strings.email_account_create_room || strings.email_new_room_title || "Create new project")
                  : (strings.email_classify_associate || "Associate to project")}
            </span>
          </button>
        </div>

        <div className="email-classify-divider">
          <span>{strings.or || "or"}</span>
        </div>

        <div className="email-classify-section">
          <div className="email-classify-question">
            {strings.email_classify_question_spam || "Is this SPAM or Advertising?"}
          </div>
          <button
            className="kj-btn kj-btn--outline kj-btn--danger kj-btn--soft-hover kj-btn--block email-classify-btn"
            onClick={() => runClassify("spam")}
            disabled={busy}
          >
            {busy && busyDecision === "spam" ? (
              <span className="email-classify-btn-spinner email-assign-modal__spinner--danger" aria-hidden="true" />
            ) : (
              <MaterialIcon icon="report" size={16} />
            )}
            <span>
              {busy && busyDecision === "spam"
                ? (strings.email_associating_spam || "Moving to Spam...")
                : (strings.email_classify_mark_spam || "Move to Spam")}
            </span>
          </button>
        </div>

        {busy && (
          <EmailClassifyBusyOverlay
            label={classifyProgressLabel(progress || { phase: busyDecision === "spam" ? "spam" : "saving" }, strings)}
            completed={progress && progress.completed}
            total={progress && progress.total}
          />
        )}
      </div>

      {confirm && (
        <ProjectConfirmModal
          typedName={projectName.trim()}
          suggestion={confirm.suggestion}
          onUseExisting={() => {
            const b = confirm.suggestion;
            setConfirm(null);
            setProjectName(b.name);
            setSelectedBuildingId(b.id);
            runClassify("project", b.id);
          }}
          onCreateNew={() => {
            setConfirm(null);
            setNewRoom(true);
          }}
          onClose={() => setConfirm(null)}
          strings={strings}
        />
      )}

      {newRoom && (
        <NewRoomModal
          initialName={projectName.trim()}
          busy={busy}
          progress={progress}
          onCreate={(name, companyId, isInternal) => runClassify("project", null, companyId, name, isInternal)}
          onClose={() => setNewRoom(false)}
          strings={strings}
        />
      )}
    </div>
  );
}

export default EmailAssignModal;
